import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { Asset } from '../api/types'
import {
  Card,
  EmptyState,
  ErrorBanner,
  formatDay,
  Spinner,
  StatCard,
} from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { colonnaTag, useTagDi } from '../components/Tag'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Registro dei beni.
 *
 * Distinto dai Configuration Item: là c'è ciò che la discovery osserva, qui ciò che
 * si possiede. La differenza si vede in ciò che questa pagina mostra e la CMDB non
 * può — proprietario, centro di costo, valore, copertura contrattuale — e in due
 * numeri che sono i soli a dire se il registro sta al passo con la realtà: quanti CI
 * non sono ancora stati registrati, e quanti beni sono senza contratto.
 */
export default function Assets() {
  const [parametri] = useSearchParams()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [category, setCategory] = useState('')
  const [uncovered, setUncovered] = useState(false)
  // The number cards open the list they count: all registered assets, or
  // only those without a contract (field report, 02/10/2026).
  function mostraBeni(soloSenzaContratto: boolean) {
    setQ('')
    setStatus('')
    setCategory('')
    setUncovered(soloSenzaContratto)
    document.getElementById('elenco-beni')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const tag = useTagDi('asset')

  const summary = useAsync(() => api.assetSummary(), [])
  const catalog = useAsync(() => api.assetCatalog(), [])
  const assets = useAsync(
    () => api.assets({ q: q || undefined, status: status || undefined, category: category || undefined, uncovered,
                       cost_center_id: parametri.get('cost_center_id') ?? undefined,
                       org_unit_id: parametri.get('org_unit_id') ?? undefined,
                       location_id: parametri.get('location_id') ?? undefined }),
    [q, status, category, uncovered, parametri.toString()],
  )

  const s = summary.data
  const righe = assets.data ?? []
  const statuses = catalog.data?.statuses ?? []
  // The tags come from the shared catalogue: the «Tag» column filters on the
  // «key:value» text and edits them in place.
  const colonne: Column<Asset>[] = [
    {
      key: 'asset_tag',
      header: t('Cartellino'),
      mono: true,
      render: (a) => <Link to={`/assets/${a.id}`}>{a.asset_tag}</Link>,
    },
    {
      key: 'bene',
      header: t('Bene'),
      value: (a) =>
        [a.name ?? t('(senza nome)'), [a.vendor, a.model].filter(Boolean).join(' ') || a.category, a.ci_id ? a.ci_name : '']
          .filter(Boolean)
          .join(' '),
      render: (a) => (
        <>
          {a.name ?? t('(senza nome)')}
          <div className="text-muted" style={{ fontSize: 11 }}>
            {[a.vendor, a.model].filter(Boolean).join(' ') || a.category}
            {/* Il collegamento al CI, quando c'è: è la differenza fra un bene che la
                rete conferma e uno di cui abbiamo solo la scheda. */}
            {a.ci_id && (
              <>
                {' · '}
                <Link to={`/cis/${a.ci_id}`}>{a.ci_name ?? t('CI collegato')}</Link>
              </>
            )}
          </div>
        </>
      ),
    },
    ...colonnaTag<Asset>('asset', tag, (a) => a.id),
    {
      key: 'status',
      header: t('Stato'),
      // L'etichetta, non il valore interno: «in_use» è come lo chiama il database, e
      // mostrarlo a chi legge scarica su di lui la traduzione.
      value: (a) => statuses.find((x) => x.value === a.status)?.label ?? a.status,
      render: (a) => (
        <span className={`badge badge--${a.status === 'in_use' ? 'ok' : 'warn'}`}>
          {statuses.find((x) => x.value === a.status)?.label ?? a.status}
        </span>
      ),
    },
    {
      key: 'owner',
      header: t('Proprietario'),
      value: (a) => [a.owner, a.cost_center].filter(Boolean).join(' '),
      render: (a) => (
        <>
          {a.owner ?? '—'}
          {a.cost_center && (
            <div className="text-muted mono" style={{ fontSize: 11 }}>
              {a.cost_center}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'purchase_date',
      header: t('Acquisto'),
      type: 'date',
      value: (a) => a.purchase_date,
      render: (a) => (
        <>
          {formatDay(a.purchase_date)}
          {a.purchase_price !== null && (
            <div className="text-muted" style={{ fontSize: 11 }}>
              {a.purchase_price.toLocaleString(localeCorrente())} {a.currency}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'warranty_until',
      header: t('Garanzia'),
      type: 'date',
      value: (a) => a.warranty_until,
      render: (a) => {
        const warranty = a.warranty_until ? new Date(a.warranty_until) : null
        const daysLeft =
          warranty && !Number.isNaN(warranty.getTime())
            ? Math.round((warranty.getTime() - Date.now()) / 86_400_000)
            : null
        if (!warranty) return '—'
        // Scaduta, con il quanto: «scaduta ieri» e «scaduta due anni fa»
        // portano a decisioni diverse.
        if (daysLeft !== null && daysLeft < 0)
          return <span style={{ color: 'var(--error)' }}>{t('scaduta da {n} g', { n: Math.abs(daysLeft) })}</span>
        if (daysLeft !== null && daysLeft < 90)
          return <span style={{ color: 'var(--warn)' }}>{t('fra {n} g', { n: daysLeft })}</span>
        return formatDay(a.warranty_until)
      },
    },
    {
      key: 'covered',
      header: t('Contratto'),
      value: (a) => (a.covered ? t('coperto') : t('scoperto')),
      render: (a) =>
        a.covered ? <span className="badge badge--ok">{t('coperto')}</span> : <span className="badge badge--warn">{t('scoperto')}</span>,
    },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Asset')}</h1>
          <p className="page__subtitle">
            {t('Raccoglie gli asset posseduti e amministrati, integrando i dati della discovery con informazioni gestionali non rilevabili dalla rete, come proprietà, costi e contratti.')}
          </p>
        </div>
      </header>

      <ErrorBanner error={assets.error ?? summary.error} />

      {s && (
        <div className="stats">
          <StatCard label={t('Beni registrati')} value={s.total} onClick={() => mostraBeni(false)} />
          <StatCard
            label={t('Valore di acquisto')}
            value={s.total_value ? `${Math.round(s.total_value).toLocaleString(localeCorrente())} €` : '—'}
            hint={t('esclusi gli alienati')}
          />
          {/* La coda di lavoro. Senza questo numero un inventario incompleto
              sembra completo: la CMDB cresce da sé, il registro dei beni no. */}
          <StatCard
            label={t('CI da registrare')}
            value={s.cis_not_promoted}
            hint={t('{n} di {tot} già registrati', { n: s.cis_promoted, tot: s.cis_total })}
            tone={s.cis_not_promoted > 0 ? 'warn' : 'ok'}
            to="/cis?unregistered=1"
          />
          <StatCard
            label={t('Senza contratto')}
            value={s.uncovered}
            onClick={() => mostraBeni(true)}
            hint={t('{n} coperti oggi', { n: s.covered })}
            tone={s.uncovered > 0 ? 'warn' : 'ok'}
          />
        </div>
      )}

      {s && s.cis_not_promoted > 0 && (
        <div className="banner banner--info">
          {(() => {
            const [prima, dopo = ''] = t('{n} CI non ancora registrati come asset. Dalla {scheda} se ne consente la registrazione, precompilando i dati già acquisiti dalla discovery (matricola, costruttore, modello e collocazione).', { n: s.cis_not_promoted }).split('{scheda}')
            return (
              <>
                {prima}
                <Link to="/cis">{t('scheda di un CI')}</Link>
                {dopo}
              </>
            )
          })()}
        </div>
      )}

      <Card title={t('Filtri')}>
        <div className="row">
          <div className="field">
            <label htmlFor="a-q">{t('Cerca')}</label>
            <input
              id="a-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('cartellino, nome o matricola')}
            />
          </div>
          <div className="field" style={{ maxWidth: 200 }}>
            <label htmlFor="a-status">{t('Stato')}</label>
            <select id="a-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('Tutti')}</option>
              {(catalog.data?.statuses ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ maxWidth: 200 }}>
            <label htmlFor="a-cat">{t('Categoria')}</label>
            <select id="a-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{t('Tutte')}</option>
              {(catalog.data?.categories ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="checkbox">
          <input
            id="a-uncovered"
            type="checkbox"
            checked={uncovered}
            onChange={(e) => setUncovered(e.target.checked)}
          />
          <label htmlFor="a-uncovered">
            {t('Solo i beni senza contratto')}
            <small>
              {t('Gli asset con contratto cessato o copertura scaduta sono considerati non coperti.')}
            </small>
          </label>
        </div>
      </Card>

      <div id="elenco-beni" />
      <Card title={t('Beni ({n})', { n: assets.data?.length ?? 0 })}>
        {assets.loading && !assets.data ? (
          <Spinner />
        ) : !assets.data?.length ? (
          <EmptyState
            title={t('Nessun bene trovato')}
            hint={t('Registra un CI come asset dalla relativa scheda oppure modifica i filtri per ampliare i risultati.')}
          />
        ) : (
          <DataTable rows={righe} columns={colonne} rowKey={(a) => a.id} conteggio={{ nome: t('asset') }} />
        )}
      </Card>
    </>
  )
}
